/**
 * Chime waiting room (design Decision 34): one `Task` per person joining, `focus` = the Appointment.
 *
 * Status: `requested` (waiting), `accepted` (admitted), `in-progress` (credentials issued), `completed` (left or
 * ended), `cancelled` with `statusReason` text `removed`. `for` is the Patient or RelatedPerson when known. A link
 * guest carries their opaque handle as an identifier, their typed name as `requester.display`, and `lastModified`
 * as last seen; a personal-link participant's attested location rides on `input` until admission.
 *
 * Only the Chime bots create or change these Tasks. Pure helpers: no Medplum client.
 */
import type { Task } from '@medplum/fhirtypes';

import { CHIME_GUEST_HANDLE_IDENTIFIER_SYSTEM, CHIME_WAITING_ROOM_TASK_CODE, TASK_TYPE_SYSTEM } from '../constants';
// Type only: a runtime import would pull the location module into every bot that reads the waiting room.
import type { LocationConfirmationInput } from './client-location-confirmation';

export type ChimeWaitingStatus = 'requested' | 'accepted' | 'in-progress' | 'completed' | 'cancelled';

/** A waiting row's attested location: the validated input plus the instant it was attested. */
export type ChimeGuestLocation = LocationConfirmationInput & { attestedAt?: string };

/** One person in (or through) the waiting room, read from their Task. */
export interface ChimeWaitingEntry {
  taskId?: string;
  status: ChimeWaitingStatus;
  /** `Patient/<id>` or `RelatedPerson/<id>`: a portal client, a personal-link match, or a guest marked as a client. */
  forRef?: string;
  /** A link joiner's opaque handle (see `isChimeGuestHandle`); absent for a portal client. */
  handle?: string;
  /** Sanitized display name: typed by a link guest, or the matched participant's name. */
  name?: string;
  /** Last poll (UTC instant): the Task's `lastModified`. */
  lastSeenAt?: string;
  /** A personal-link participant's attested location, written to the Encounter once they are admitted. */
  location?: ChimeGuestLocation;
}

/** Open statuses: the person is waiting, admitted, or in the call. */
export const CHIME_WAITING_OPEN_STATUSES: readonly ChimeWaitingStatus[] = ['requested', 'accepted', 'in-progress'];

/**
 * A link guest whose browser has not polled for this long is treated as gone. Generous so a backgrounded tab (timers
 * throttled to ~1/min) survives; the bot refreshes `lastModified` at most every `CHIME_WAITING_GUEST_REFRESH_MS`.
 */
export const CHIME_WAITING_GUEST_TTL_MS = 5 * 60 * 1000;
export const CHIME_WAITING_GUEST_REFRESH_MS = 2 * 60 * 1000;
/** At most this many link guests wait at once, so a leaked link can't flood the list. */
export const CHIME_WAITING_GUESTS_MAX = 25;

/** Opaque guest handle: 32 lowercase hex chars, derived server-side from a secret join key; never the key itself. */
const GUEST_HANDLE_PATTERN = /^[0-9a-f]{32}$/;
const PARTICIPANT_REF_PATTERN = /^(Patient|RelatedPerson)\/[A-Za-z0-9\-.]{1,64}$/;
const LOCATION_INPUT_TYPE = 'location';
const STATUS_REASON_REMOVED = 'removed';

export function isChimeGuestHandle(value: string | undefined): value is string {
  return typeof value === 'string' && GUEST_HANDLE_PATTERN.test(value);
}

/** Self-declared and rendered in the provider's waiting-room list, so bound and de-fanged. */
export function sanitizeGuestName(name: string | undefined): string {
  // eslint-disable-next-line no-control-regex
  const cleaned = (name ?? '').replace(/[|\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned.slice(0, 80) || 'Guest';
}

/** True for a Chime waiting-room Task (so task lists can leave them out). */
export function isChimeWaitingRoomTask(task: Pick<Task, 'code'>): boolean {
  return Boolean(task.code?.coding?.some((c) => c.system === TASK_TYPE_SYSTEM && c.code === CHIME_WAITING_ROOM_TASK_CODE));
}

/** Search parameters for an appointment's open waiting-room Tasks. */
export function chimeWaitingTaskSearch(appointmentId: string): Record<string, string> {
  return {
    code: `${TASK_TYPE_SYSTEM}|${CHIME_WAITING_ROOM_TASK_CODE}`,
    focus: `Appointment/${appointmentId}`,
    status: CHIME_WAITING_OPEN_STATUSES.join(','),
    _count: '200',
  };
}

const LOCATION_PREFILL = new Set(['device_location', 'saved_address', 'manual', 'none', 'ip']);

/**
 * Re-checks a stored location as strictly as the bot's validator (`parseLocationConfirmationInput`, which this module
 * cannot import at runtime), so an edited value is dropped instead of breaking the admitting save. Only known keys
 * are kept.
 */
export function parseChimeGuestLocation(raw: string | undefined): ChimeGuestLocation | undefined {
  if (!raw?.trim()) return undefined;
  try {
    const v = JSON.parse(raw) as Record<string, unknown> | null;
    if (!v || typeof v !== 'object') return undefined;
    const text = (key: string, max: number, required: boolean): string | undefined | null => {
      const value = v[key];
      if (value === undefined && !required) return undefined;
      return typeof value === 'string' && value.trim() && value.length <= max ? value.trim() : null;
    };
    const street = text('street', 200, true);
    const city = text('city', 100, true);
    const state = text('state', 2, true);
    const postalCode = text('postalCode', 10, true);
    const ipDerivedCity = text('ipDerivedCity', 100, false);
    const ipDerivedState = text('ipDerivedState', 2, false);
    if (!street || !city || !state || !postalCode || ipDerivedCity === null || ipDerivedState === null) return undefined;
    if (!/^[A-Za-z]{2}$/.test(state) || v.attestationAccepted !== true) return undefined;
    if (typeof v.notAtUsualAddress !== 'boolean' || typeof v.wasEditedAfterPrefill !== 'boolean') return undefined;
    if (typeof v.prefillSource !== 'string' || !LOCATION_PREFILL.has(v.prefillSource)) return undefined;
    const attestedAt = typeof v.attestedAt === 'string' && !Number.isNaN(Date.parse(v.attestedAt)) ? v.attestedAt : undefined;
    return {
      street,
      city,
      state,
      postalCode,
      notAtUsualAddress: v.notAtUsualAddress,
      attestationAccepted: true,
      prefillSource: v.prefillSource as ChimeGuestLocation['prefillSource'],
      wasEditedAfterPrefill: v.wasEditedAfterPrefill,
      ...(ipDerivedCity ? { ipDerivedCity } : {}),
      ...(ipDerivedState ? { ipDerivedState } : {}),
      ...(attestedAt ? { attestedAt } : {}),
    };
  } catch {
    return undefined;
  }
}

/** The entry a waiting-room Task holds, or undefined for any other Task or a malformed one. */
export function chimeWaitingEntryOf(task: Task): ChimeWaitingEntry | undefined {
  if (!isChimeWaitingRoomTask(task)) return undefined;
  const status = task.status as ChimeWaitingStatus;
  if (!['requested', 'accepted', 'in-progress', 'completed', 'cancelled'].includes(status)) return undefined;
  const forRef = task.for?.reference;
  const handle = task.identifier?.find((i) => i.system === CHIME_GUEST_HANDLE_IDENTIFIER_SYSTEM)?.value;
  const location = parseChimeGuestLocation(
    task.input?.find((i) => i.type?.text === LOCATION_INPUT_TYPE)?.valueString,
  );
  return {
    ...(task.id ? { taskId: task.id } : {}),
    status,
    ...(forRef && PARTICIPANT_REF_PATTERN.test(forRef) ? { forRef } : {}),
    ...(isChimeGuestHandle(handle) ? { handle } : {}),
    ...(task.requester?.display ? { name: sanitizeGuestName(task.requester.display) } : {}),
    ...(task.lastModified ? { lastSeenAt: task.lastModified } : {}),
    ...(location ? { location } : {}),
  };
}

/** True when the entry was removed by the provider (`cancelled`, reason `removed`). */
export function isChimeWaitingEntryRemoved(task: Pick<Task, 'status' | 'statusReason'>): boolean {
  return task.status === 'cancelled' && task.statusReason?.text === STATUS_REASON_REMOVED;
}

/** A link guest who hasn't polled within the TTL counts as gone; a portal client (no handle) never goes stale this way. */
export function isChimeWaitingEntryStale(entry: ChimeWaitingEntry, nowMs: number): boolean {
  if (!entry.handle || entry.status === 'in-progress') return false;
  const seen = entry.lastSeenAt ? Date.parse(entry.lastSeenAt) : Number.NaN;
  return Number.isNaN(seen) || nowMs - seen > CHIME_WAITING_GUEST_TTL_MS;
}

/**
 * The Task for `entry` on `appointmentId`, with `lastModified` = `nowIso`. Keeps `id` when the entry has one. A
 * `cancelled` entry is marked `removed`.
 */
export function chimeWaitingTask(appointmentId: string, entry: ChimeWaitingEntry, nowIso: string): Task {
  if (entry.forRef && !PARTICIPANT_REF_PATTERN.test(entry.forRef)) throw new Error('Invalid waiting-room participant');
  if (entry.handle !== undefined && !isChimeGuestHandle(entry.handle)) throw new Error('Invalid waiting-room handle');
  return {
    resourceType: 'Task',
    ...(entry.taskId ? { id: entry.taskId } : {}),
    status: entry.status,
    intent: 'order',
    code: { coding: [{ system: TASK_TYPE_SYSTEM, code: CHIME_WAITING_ROOM_TASK_CODE }] },
    focus: { reference: `Appointment/${appointmentId}` },
    ...(entry.forRef ? { for: { reference: entry.forRef } } : {}),
    ...(entry.handle ? { identifier: [{ system: CHIME_GUEST_HANDLE_IDENTIFIER_SYSTEM, value: entry.handle }] } : {}),
    ...(entry.name ? { requester: { display: sanitizeGuestName(entry.name) } } : {}),
    ...(entry.status === 'cancelled' ? { statusReason: { text: STATUS_REASON_REMOVED } } : {}),
    lastModified: nowIso,
    ...(entry.location
      ? { input: [{ type: { text: LOCATION_INPUT_TYPE }, valueString: JSON.stringify(entry.location) }] }
      : {}),
  };
}
